/**
 * Pitch limit A/B/C test helpers.
 *
 * Mirrors the server-side logic in PitchLimitTest.php:
 *   - The feature toggle must be enabled (featureToggles.pitchLimitAbcTest).
 *   - Only users who joined on or after TEST_START_DATE are bucketed.
 *   - Variant is determined by user.id % 3:
 *       0 → 'a' (control: 1 pitch/month)
 *       1 → 'b' (1 pitch/day)
 *       2 → 'c' (5 pitches/month)
 *
 * On non-production environments the toggle and date requirements are bypassed
 * so the test can be verified on staging before going live.
 *
 * Returns null when the toggle is off, or for pre-test users.
 */

import featureToggles from 'featureToggles';

const PITCH_LIMIT_TEST_START_DATE = new Date('2026-05-06T00:00:00Z');

/**
 * Returns the A/B/C test variant for a user, or null if the toggle is off
 * or the user pre-dates the test.
 *
 * On non-production environments the feature toggle and join-date requirements
 * are skipped so the test can be verified on staging before going live.
 *
 * @param {{ id: number, joined: string }} user
 * @returns {'a'|'b'|'c'|null}
 */
export const getPitchLimitTestVariant = (user) => {
  if (!user || !user.joined) return null;

  if (/production/.test(__CLOUD_ENV__)) {
    if (!featureToggles.pitchLimitAbcTest) return null;

    const joinedDate = new Date(user.joined);
    if (joinedDate < PITCH_LIMIT_TEST_START_DATE) return null;
  }

  const variants = ['a', 'b', 'c'];
  return variants[user.id % 3];
};

/**
 * Returns a human-readable string for time until midnight UTC.
 * Used to tell variant B users when their daily allowance resets.
 *
 * Examples: "23 hours", "1 hour", "45 minutes", "1 minute"
 *
 * @param {Date} [now] - Defaults to the current time.
 * @returns {string}
 */
export const timeUntilMidnightUTC = (now = new Date()) => {
  const midnight = new Date(now);
  midnight.setUTCHours(24, 0, 0, 0);
  const msLeft = midnight - now;
  const minutesLeft = Math.max(1, Math.round(msLeft / 60000));

  if (minutesLeft >= 60) {
    const hours = Math.round(minutesLeft / 60);
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return minutesLeft === 1 ? '1 minute' : `${minutesLeft} minutes`;
};

/**
 * Returns the free starter pitch allowance copy for the given variant.
 * Used in PricingBreakdownTable starter column.
 *
 * @param {'a'|'b'|'c'|null} variant
 * @returns {string}
 */
export const getStarterPitchAllowanceCopy = (variant) => {
  if (variant === 'b') return '1 per day';
  if (variant === 'c') return '5';
  return '1';
};

/**
 * Returns the starter pitch allowance description for the pricing breakdown feature row.
 * Used in PricingBreakdownCreative.
 *
 * @param {'a'|'b'|'c'|null} variant
 * @returns {string}
 */
export const getStarterPitchFeatureDescription = (variant) => {
  if (variant === 'b') {
    return '1 application a day, for jobs with fewer applicants. Resets every 24 hours.';
  }
  if (variant === 'c') {
    return '5 free applications per month to jobs that are not popular.';
  }
  return '1 free applications per month to jobs that are not popular.';
};

/**
 * Returns the limit-exhausted body copy for the "no more pitches" panel.
 * Variant B shows a time-until-reset string.
 *
 * @param {'a'|'b'|'c'|null} variant
 * @param {string} resetIn - Human-readable time until reset (e.g. "3 hours"). Only used for variant B.
 * @returns {string}
 */
export const getLimitExhaustedCopy = (variant, resetIn) => {
  if (variant === 'b') {
    return `You've used today's application. Next one resets in ${resetIn}.`;
  }
  return 'You have reached your limited amount of monthly applications on your account.';
};
